/**
 * Pure helpers for the skill pages: the skills hub, the skill page, the career skill map, the
 * foundations pathway, learning analytics and the contextual skill panel.
 *
 * Nothing here fetches or renders. Mastery is computed by the server from evidence; these helpers
 * only order, group, lay out and describe what the API returned, so the wording stays honest
 * (practice indicators, never qualifications) and every rule is testable.
 */
import { SKILL_DOMAINS, SKILL_DOMAIN_LABELS, type MasteryState, type SkillCounters, type SkillDomain } from '@worksim/engine';
import type { IconName } from '../components/ui/Icon';
import { href } from '../router';
import type {
  CareerSkillMapView,
  FoundationSkillView,
  MasteryChange,
  MasteryHistoryEntry,
  MasteryRowView,
  SkillEvidenceView,
  SkillGraphView,
  SkillUsageView,
} from '../api/skills';

/* ------------------------------------------------------------------ states */

/**
 * Progress rank of a state, the same order the engine uses to describe a change: learning sits
 * below introduced, and needs-review sits between practicing and demonstrated.
 */
export const MASTERY_RANK: Record<MasteryState, number> = {
  'not-started': 0,
  learning: 1,
  introduced: 2,
  practicing: 3,
  'needs-review': 4,
  demonstrated: 5,
  'strong-evidence': 6,
};

export const rankOf = (state: MasteryState | undefined): number => MASTERY_RANK[state ?? 'not-started'] ?? 0;

/** True when `state` is at `min` or further along. A missing state counts as not started. */
export const atLeast = (state: MasteryState | undefined, min: MasteryState): boolean => rankOf(state) >= rankOf(min);

/** Demonstrated or strong evidence: the states the foundations summary counts. */
export const isDemonstrated = (state: MasteryState | undefined): boolean => state === 'demonstrated' || state === 'strong-evidence';

/** Skill interview practice opens once the skill has been practiced hands-on (the server decides; this only mirrors it). */
export const canPracticeInterview = (state: MasteryState | undefined): boolean => atLeast(state, 'practicing');

/* ------------------------------------------------------------------ map legend */

export type MapTone = 'mastered' | 'practicing' | 'review' | 'new';

/** The four groups the skill map colors by. Every node also prints its state in words. */
export const MAP_LEGEND: { tone: MapTone; label: string; states: MasteryState[] }[] = [
  { tone: 'mastered', label: 'Mastered (strong evidence)', states: ['strong-evidence'] },
  { tone: 'practicing', label: 'Practicing or demonstrated', states: ['practicing', 'demonstrated'] },
  { tone: 'review', label: 'Needs review', states: ['needs-review'] },
  { tone: 'new', label: 'Not started, learning or introduced', states: ['not-started', 'learning', 'introduced'] },
];

export function mapTone(state: MasteryState | undefined): MapTone {
  const s = state ?? 'not-started';
  return MAP_LEGEND.find((g) => g.states.includes(s))?.tone ?? 'new';
}

/* ------------------------------------------------------------------ domains and search */

export function domainLabel(domain: string | undefined): string {
  if (!domain) return 'General';
  return SKILL_DOMAIN_LABELS[domain as SkillDomain] ?? domain.replace(/[-_]/g, ' ');
}

interface Searchable {
  id: string;
  name: string;
  description?: string;
  plainEnglish?: string;
  domain?: string;
  category?: string;
  foundation?: boolean;
  aliases?: string[];
}

export interface SkillFilter {
  query?: string;
  domains?: string[];
  foundationOnly?: boolean;
}

/** Skills matching a search (name, aliases, description, domain) and the domain and foundation filters. */
export function filterSkills<T extends Searchable>(list: T[], filter: SkillFilter): T[] {
  const q = (filter.query ?? '').trim().toLowerCase();
  const domains = filter.domains ?? [];
  return list.filter((s) => {
    if (filter.foundationOnly && !s.foundation) return false;
    if (domains.length > 0 && !domains.includes(s.domain ?? '')) return false;
    if (!q) return true;
    const hay = [s.id, s.name, s.description, s.plainEnglish, s.category, domainLabel(s.domain), ...(s.aliases ?? [])].filter(Boolean).join(' ').toLowerCase();
    return q.split(/\s+/).every((word) => hay.includes(word));
  });
}

/** Domains that at least one skill uses, in the platform's domain order, with counts. */
export function domainsInUse(list: { domain?: string }[]): { id: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of list) if (s.domain) counts.set(s.domain, (counts.get(s.domain) ?? 0) + 1);
  const known = SKILL_DOMAINS.filter((d) => counts.has(d.id)).map((d) => ({ id: d.id as string, label: d.label, count: counts.get(d.id) ?? 0 }));
  const extra = [...counts.keys()].filter((id) => !SKILL_DOMAINS.some((d) => d.id === id)).sort().map((id) => ({ id, label: domainLabel(id), count: counts.get(id) ?? 0 }));
  return [...known, ...extra];
}

/* ------------------------------------------------------------------ where a skill is used */

export interface UsageKindMeta {
  singular: string;
  plural: string;
  icon: IconName;
}

export const USAGE_KIND: Record<string, UsageKindMeta> = {
  lesson: { singular: 'lesson', plural: 'lessons', icon: 'book' },
  'knowledge-topic': { singular: 'knowledge topic', plural: 'knowledge topics', icon: 'lightbulb' },
  'cheat-sheet': { singular: 'cheat sheet', plural: 'cheat sheets', icon: 'clipboard' },
  lab: { singular: 'lab', plural: 'labs', icon: 'terminal' },
  'code-lab': { singular: 'code lab', plural: 'code labs', icon: 'code' },
  'vm-lab': { singular: 'VM lab', plural: 'VM labs', icon: 'terminal' },
  scenario: { singular: 'scenario', plural: 'scenarios', icon: 'briefcase' },
  project: { singular: 'project', plural: 'projects', icon: 'folder' },
  assessment: { singular: 'assessment', plural: 'assessments', icon: 'check' },
  resource: { singular: 'resource', plural: 'resources', icon: 'map' },
};

export const usageMeta = (kind: string): UsageKindMeta => USAGE_KIND[kind] ?? { singular: kind.replace(/-/g, ' '), plural: `${kind.replace(/-/g, ' ')}s`, icon: 'arrow-right' };

const enc = encodeURIComponent;

/**
 * Where an item of a given kind lives. Knowledge topics, cheat sheets, resources, code labs, VM
 * labs and assessments use the hash paths their pages publish.
 */
export function usageHref(kind: string, id: string, careerId?: string, skillId?: string): string {
  switch (kind) {
    case 'lesson':
      return href({ page: 'learn-lesson', id });
    case 'lab':
      return href({ page: 'learn-lab', id });
    case 'scenario':
      return href({ page: 'scenario', id, career: careerId });
    case 'project':
      return href({ page: 'project', id });
    case 'career':
      return href({ page: 'career', id });
    case 'certification':
      return href({ page: 'certification', id });
    case 'practice-bank':
      return href({ page: 'prep-bank', id });
    case 'skill':
      return href({ page: 'skill', id });
    case 'knowledge-topic':
      return `#/knowledge/topic/${enc(id)}`;
    case 'cheat-sheet':
      return `#/cheatsheets/${enc(id)}`;
    case 'resource':
      return skillId ? `#/resources?skill=${enc(skillId)}` : '#/resources';
    case 'code-lab':
      return `#/codelabs/${enc(id)}`;
    case 'vm-lab':
      return `#/vmlabs/${enc(id)}`;
    case 'assessment':
      return `#/assessments/${enc(id)}`;
    default:
      return href({ page: 'skills' });
  }
}

/** "Code lab" from "code lab". */
export const capitalize = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "1 lesson", "3 labs". */
export const countOf = (n: number, kind: string): string => `${n} ${n === 1 ? usageMeta(kind).singular : usageMeta(kind).plural}`;

/** How much content uses a skill, as the contextual panel summarizes it. Code and VM labs count as labs. */
export function usageCounts(usage: Pick<SkillUsageView, 'lessons' | 'labs' | 'scenarios' | 'extras'>): { lessons: number; labs: number; scenarios: number; line: string } {
  const lessons = usage.lessons.length;
  const labs = usage.labs.length + (usage.extras['code-lab']?.length ?? 0) + (usage.extras['vm-lab']?.length ?? 0);
  const scenarios = usage.scenarios.length;
  return { lessons, labs, scenarios, line: `Used in ${countOf(lessons, 'lesson')}, ${countOf(labs, 'lab')} and ${countOf(scenarios, 'scenario')}` };
}

/* ------------------------------------------------------------------ completion responses */

/** The `masteryChanges` a completion response carried, or an empty list. Tolerates any shape. */
export function masteryChangesOf(response: unknown): MasteryChange[] {
  if (typeof response !== 'object' || response === null) return [];
  const list = (response as { masteryChanges?: unknown }).masteryChanges;
  if (!Array.isArray(list)) return [];
  return list.filter((c): c is MasteryChange => typeof c === 'object' && c !== null && typeof (c as MasteryChange).skillId === 'string' && typeof (c as MasteryChange).message === 'string');
}

/* ------------------------------------------------------------------ careers */

/** Careers with the learner's enrolled tracks first (in enrollment order), then the rest by name. */
export function orderCareers<T extends { id: string; name: string }>(careers: T[], enrolled: string[]): T[] {
  const pos = new Map(enrolled.map((id, i) => [id, i]));
  return [...careers].sort((a, b) => {
    const pa = pos.get(a.id);
    const pb = pos.get(b.id);
    if (pa !== undefined && pb !== undefined) return pa - pb;
    if (pa !== undefined) return -1;
    if (pb !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
}

/* ------------------------------------------------------------------ skill map layout */

export const MAP_NODE_W = 236;
export const MAP_NODE_H = 54;
const COL_GAP = 76;
const ROW_GAP = 14;
const PAD_X = 40;
const HEAD = 40;
const PAD_BOTTOM = 16;

export const TIER_LABEL: Record<string, string> = { foundational: 'Foundational', intermediate: 'Intermediate', advanced: 'Advanced' };

export interface MapNode {
  skillId: string;
  name: string;
  tier: string;
  column: number;
  row: number;
  x: number;
  y: number;
  state: MasteryState | undefined;
  tone: MapTone;
  foundation: boolean;
  domain?: string;
  prerequisiteSkillIds: string[];
}

export interface MapEdge {
  from: string;
  to: string;
  d: string;
}

export interface MapLayout {
  width: number;
  height: number;
  columns: { tier: string; label: string; x: number; count: number }[];
  nodes: MapNode[];
  edges: MapEdge[];
}

/**
 * Lay a career skill map out in three columns (foundational, intermediate, advanced), each in
 * prerequisite order, with one curved edge per prerequisite link inside the career. A skill that
 * appears in two tiers is drawn once, in the first.
 */
export function layoutSkillMap(map: CareerSkillMapView, stateOf: (skillId: string) => MasteryState | undefined): MapLayout {
  const nodes: MapNode[] = [];
  const seen = new Set<string>();
  const columns = map.tiers.map((t, column) => {
    const x = PAD_X + column * (MAP_NODE_W + COL_GAP);
    let row = 0;
    for (const s of t.skills) {
      if (seen.has(s.skillId)) continue;
      seen.add(s.skillId);
      const state = stateOf(s.skillId);
      nodes.push({ skillId: s.skillId, name: s.name, tier: t.tier, column, row, x, y: HEAD + row * (MAP_NODE_H + ROW_GAP), state, tone: mapTone(state), foundation: Boolean(s.foundation), domain: s.domain, prerequisiteSkillIds: s.prerequisiteSkillIds });
      row += 1;
    }
    return { tier: t.tier, label: TIER_LABEL[t.tier] ?? t.tier, x, count: row };
  });
  const byId = new Map(nodes.map((n) => [n.skillId, n]));
  const edges: MapEdge[] = [];
  for (const n of nodes) {
    for (const p of n.prerequisiteSkillIds) {
      const from = byId.get(p);
      if (!from) continue;
      edges.push({ from: from.skillId, to: n.skillId, d: edgePath(from, n) });
    }
  }
  const rows = Math.max(1, ...columns.map((c) => c.count));
  const width = PAD_X * 2 + Math.max(1, columns.length) * MAP_NODE_W + Math.max(0, columns.length - 1) * COL_GAP;
  const height = HEAD + rows * MAP_NODE_H + (rows - 1) * ROW_GAP + PAD_BOTTOM;
  return { width, height, columns, nodes, edges };
}

function edgePath(a: MapNode, b: MapNode): string {
  const ay = a.y + MAP_NODE_H / 2;
  const by = b.y + MAP_NODE_H / 2;
  if (a.column < b.column) {
    const x1 = a.x + MAP_NODE_W;
    const x2 = b.x;
    const mid = (x1 + x2) / 2;
    return `M${x1},${ay} C${mid},${ay} ${mid},${by} ${x2},${by}`;
  }
  if (a.column > b.column) {
    const x1 = a.x;
    const x2 = b.x + MAP_NODE_W;
    const mid = (x1 + x2) / 2;
    return `M${x1},${ay} C${mid},${ay} ${mid},${by} ${x2},${by}`;
  }
  // Same column: a bow on the left side, so the link never crosses the column's nodes.
  const bow = a.x - 30;
  return `M${a.x},${ay} C${bow},${ay} ${bow},${by} ${a.x},${by}`;
}

/** Shorten a name to fit a node line, on a word boundary where possible. */
export function clip(text: string, max = 28): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/* ------------------------------------------------------------------ foundations */

export interface LadderStep extends FoundationSkillView {
  /** Every prerequisite is at least introduced (always true when signed out: nothing is known). */
  unlocked: boolean;
  /** The first prerequisite that is not introduced yet, when signed in. */
  recommendedFirst?: { skillId: string; name: string };
  prerequisites: { skillId: string; name: string }[];
}

/**
 * The foundations pathway as a ladder. Nothing is ever blocked: a step whose prerequisites are not
 * yet introduced says which one is recommended first, and still links through.
 */
export function foundationsLadder(steps: FoundationSkillView[], signedIn: boolean, nameOf: (skillId: string) => string | undefined = () => undefined): LadderStep[] {
  const byId = new Map(steps.map((s) => [s.skillId, s]));
  const name = (id: string) => byId.get(id)?.name ?? nameOf(id) ?? id.replace(/-/g, ' ');
  return steps.map((s) => {
    const prerequisites = s.prerequisiteSkillIds.map((id) => ({ skillId: id, name: name(id) }));
    if (!signedIn) return { ...s, unlocked: true, prerequisites };
    const pending = s.prerequisiteSkillIds.filter((id) => !atLeast(byId.get(id)?.state, 'introduced'));
    const first = pending[0];
    return { ...s, unlocked: pending.length === 0, prerequisites, ...(first ? { recommendedFirst: { skillId: first, name: name(first) } } : {}) };
  });
}

/** "n of m foundations at demonstrated or better". */
export function foundationsSummary(steps: { state?: MasteryState }[]): { done: number; total: number; ratio: number } {
  const done = steps.filter((s) => isDemonstrated(s.state)).length;
  return { done, total: steps.length, ratio: steps.length > 0 ? done / steps.length : 0 };
}

/**
 * The first lesson (or, failing that, knowledge topic) that teaches each skill, read from the
 * skill graph's `teaches` edges in the order the graph lists them. `lessonRank` prefers some
 * lessons over others (lower first, for example beginner-level lessons); ties keep graph order.
 */
export function firstTeachingItems(graph: SkillGraphView, lessonRank: (lessonId: string) => number = () => 0): Map<string, { kind: 'lesson' | 'knowledge-topic'; id: string; title: string }> {
  const nodes = new Map(graph.nodes.map((n) => [n.key, n]));
  const lessons = new Map<string, { kind: 'lesson'; id: string; title: string }>();
  const topics = new Map<string, { kind: 'knowledge-topic'; id: string; title: string }>();
  for (const e of graph.edges) {
    if (e.kind !== 'teaches' || !e.to.startsWith('skill:')) continue;
    const skillId = e.to.slice('skill:'.length);
    const from = nodes.get(e.from);
    if (!from) continue;
    const current = lessons.get(skillId);
    if (from.kind === 'lesson' && (!current || lessonRank(from.id) < lessonRank(current.id))) lessons.set(skillId, { kind: 'lesson', id: from.id, title: from.title });
    if (from.kind === 'knowledge-topic' && !topics.has(skillId)) topics.set(skillId, { kind: 'knowledge-topic', id: from.id, title: from.title });
  }
  const out = new Map<string, { kind: 'lesson' | 'knowledge-topic'; id: string; title: string }>();
  for (const [id, item] of topics) out.set(id, item);
  for (const [id, item] of lessons) out.set(id, item);
  return out;
}

/* ------------------------------------------------------------------ evidence timeline */

export const SOURCE_LABEL: Record<string, string> = {
  lesson: 'Lesson',
  lab: 'Lab',
  scenario: 'Scenario',
  project: 'Project',
  interview: 'Interview practice',
  practice: 'Certification practice',
  'code-lab': 'Code lab',
  'vm-lab': 'VM lab',
  assessment: 'Assessment',
  'knowledge-check': 'Knowledge check',
  transfer: 'Carried from another track',
  rebuild: 'Recomputed from your records',
  refresh: 'Refreshed as evidence aged',
  recompute: 'Recomputed from your records',
};

export const sourceLabel = (source: string): string => SOURCE_LABEL[source] ?? source.replace(/-/g, ' ');

export interface TimelineItem {
  at: string;
  kind: 'change' | 'evidence';
  title: string;
  detail: string;
  direction?: 'up' | 'down' | 'same';
}

export function directionOf(from: MasteryState | undefined, to: MasteryState): 'up' | 'down' | 'same' {
  const a = rankOf(from);
  const b = rankOf(to);
  return b > a ? 'up' : b < a ? 'down' : 'same';
}

/** State changes and evidence pieces for one skill on a single timeline, newest first. */
export function evidenceTimeline(changes: MasteryHistoryEntry[], evidence: SkillEvidenceView[], limit = 12): TimelineItem[] {
  const items: TimelineItem[] = [
    ...changes.map((h) => ({
      at: h.at,
      kind: 'change' as const,
      title: h.fromLabel ? `Moved from ${h.fromLabel} to ${h.toLabel}` : `Recorded as ${h.toLabel}`,
      detail: sourceLabel(h.source),
      direction: directionOf(h.from, h.to),
    })),
    ...evidence.map((e) => ({
      at: e.at,
      kind: 'evidence' as const,
      title: e.title,
      detail: `${sourceLabel(e.kind)}${e.score !== undefined ? ` · scored ${Math.round(e.score * 100)}%` : ''}`,
    })),
  ];
  return items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
}

/* ------------------------------------------------------------------ analytics */

export const COUNTER_LABEL: { key: keyof SkillCounters; label: string }[] = [
  { key: 'lessonsCompleted', label: 'Lessons passed' },
  { key: 'knowledgeChecksPassed', label: 'Knowledge checks passed' },
  { key: 'labsCompleted', label: 'Labs completed' },
  { key: 'codeLabsCompleted', label: 'Code labs completed' },
  { key: 'vmLabsCompleted', label: 'VM labs completed' },
  { key: 'scenariosCompleted', label: 'Scenarios completed' },
  { key: 'projectsCompleted', label: 'Projects completed' },
  { key: 'interviewAttempts', label: 'Interview practice sessions' },
  { key: 'assessmentsPassed', label: 'Assessments passed' },
];

/** Sum of every counter across the learner's skills. Work that names several skills counts once per skill. */
export function sumCounters(rows: { counters?: Partial<SkillCounters> }[]): SkillCounters {
  const out: SkillCounters = { lessonsCompleted: 0, lessonsStarted: 0, labsCompleted: 0, codeLabsCompleted: 0, vmLabsCompleted: 0, scenariosCompleted: 0, projectsCompleted: 0, interviewAttempts: 0, assessmentsPassed: 0, knowledgeChecksPassed: 0, hintsUsed: 0, retries: 0, minutes: 0 };
  for (const r of rows) for (const k of Object.keys(out) as (keyof SkillCounters)[]) out[k] += Number(r.counters?.[k] ?? 0) || 0;
  return out;
}

/** Counters that describe one skill in a few words ("2 lessons · 1 lab · 15 min"), or an empty string. */
export function countersLine(c: Partial<SkillCounters> | undefined): string {
  if (!c) return '';
  const parts: string[] = [];
  const add = (n: number | undefined, kind: string) => {
    if (n && n > 0) parts.push(countOf(n, kind));
  };
  add(c.lessonsCompleted, 'lesson');
  add((c.labsCompleted ?? 0) + (c.codeLabsCompleted ?? 0) + (c.vmLabsCompleted ?? 0), 'lab');
  add(c.scenariosCompleted, 'scenario');
  add(c.projectsCompleted, 'project');
  if (c.interviewAttempts) parts.push(`${c.interviewAttempts} interview${c.interviewAttempts === 1 ? '' : 's'}`);
  if (c.minutes) parts.push(minutesText(c.minutes));
  return parts.join(' · ');
}

export function minutesText(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

export interface DomainStrength {
  domain: string;
  label: string;
  total: number;
  demonstrated: number;
  share: number;
}

export interface AnalyticsSummary {
  counts: Record<MasteryState, number>;
  total: number;
  demonstrated: number;
  recent: MasteryHistoryEntry[];
  movedUp: number;
  movedDown: number;
  effort: SkillCounters;
  strongestDomains: DomainStrength[];
  weakAreas: MasteryRowView[];
}

const DAY = 86_400_000;

/**
 * The analytics view, computed from the learner's snapshot and history: states today, changes in
 * the last `days` days, effort counters, the domains with the most demonstrated skills (a
 * description of the learner's own evidence, never a ranking of careers) and the weak areas.
 */
export function analyticsSummary(rows: MasteryRowView[], history: MasteryHistoryEntry[], now: Date, days = 30): AnalyticsSummary {
  const counts: Record<MasteryState, number> = { 'not-started': 0, learning: 0, introduced: 0, practicing: 0, 'needs-review': 0, demonstrated: 0, 'strong-evidence': 0 };
  for (const r of rows) counts[r.state] = (counts[r.state] ?? 0) + 1;
  const since = now.getTime() - days * DAY;
  const recent = history.filter((h) => new Date(h.at).getTime() >= since);
  const movedUp = recent.filter((h) => directionOf(h.from, h.to) === 'up').length;
  const movedDown = recent.filter((h) => directionOf(h.from, h.to) === 'down').length;
  const byDomain = new Map<string, { total: number; demonstrated: number }>();
  for (const r of rows) {
    const key = r.domain ?? '';
    if (!key) continue;
    const d = byDomain.get(key) ?? { total: 0, demonstrated: 0 };
    d.total += 1;
    if (isDemonstrated(r.state)) d.demonstrated += 1;
    byDomain.set(key, d);
  }
  const strongestDomains = [...byDomain.entries()]
    .map(([domain, d]) => ({ domain, label: domainLabel(domain), total: d.total, demonstrated: d.demonstrated, share: d.total ? d.demonstrated / d.total : 0 }))
    .filter((d) => d.demonstrated > 0)
    .sort((a, b) => b.demonstrated - a.demonstrated || b.share - a.share || a.label.localeCompare(b.label))
    .slice(0, 3);
  const weakAreas = rows
    .filter((r) => r.state === 'needs-review' || r.state === 'practicing')
    .sort((a, b) => (a.state === b.state ? a.mastery - b.mastery || a.name.localeCompare(b.name) : a.state === 'needs-review' ? -1 : 1));
  return {
    counts,
    total: rows.length,
    demonstrated: rows.filter((r) => isDemonstrated(r.state)).length,
    recent,
    movedUp,
    movedDown,
    effort: sumCounters(rows),
    strongestDomains,
    weakAreas,
  };
}
